import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone, Star, CheckCircle2, ArrowRight, Shield, Droplets, Camera, Wrench, Sparkles, Settings, Building, MapPin,
} from "lucide-react";
import { api } from "../lib/api";
import CTASection from "../components/CTASection";
import { PoolTipsTeaser } from "./PoolTipsPage";

const iconMap = { Droplets, Camera, Wrench, Sparkles, Settings, Building };

// Pool Guru mascot — pink flamingo (transparent PNG, served from /public)
const MASCOT_URL = "/flamingo.png";

export const Mascot = ({ size = 32, className = "", testId }) => (
  <img
    src={MASCOT_URL}
    alt="Pool Guru flamingo mascot"
    width={size}
    height={size}
    className={`inline-block select-none drop-shadow-md ${className}`}
    style={{ width: size, height: size, objectFit: "contain" }}
    data-testid={testId || "mascot-flamingo"}
    loading="lazy"
  />
);

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [cities, setCities] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    document.title = "The Pool Guru Treasure Coast | Pool Cleaning Port St. Lucie FL";
    Promise.all([
      api.get("/services"),
      api.get("/cities"),
      api.get("/reviews?featured=true"),
    ])
      .then(([s, c, r]) => {
        setServices(s.data);
        setCities(c.data);
        setReviews(r.data);
      })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-navy min-h-[820px] sm:min-h-[640px] lg:min-h-[860px]">
        {/* Mobile background — full-width photo with light scrim */}
        <div className="absolute inset-0 lg:hidden">
          <img
            src="/hero-pool-brandpink.jpg"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-bottom"
          />
          <div className="absolute inset-0 pointer-events-none
                          bg-[linear-gradient(180deg,rgba(10,25,47,0.65)_0%,rgba(10,25,47,0.25)_25%,rgba(10,25,47,0.05)_50%,rgba(10,25,47,0.3)_80%,rgba(10,25,47,0.65)_100%)]" />
        </div>

        {/* Desktop background — solid navy + smaller photo anchored to the right */}
        <div className="hidden lg:block absolute inset-0">
          {/* Soft brand gradient backdrop on the left for clean text contrast */}
          <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy to-[#0d2a4f]" />
          {/* Subtle pool-ripple texture behind text (low opacity for depth) */}
          <img
            src="/hero-pool-brandpink.jpg"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-[0.04]"
          />
          {/* The "guy on the float" — sized small, anchored right, fully clear of text */}
          <div className="absolute top-0 bottom-0 right-0 w-[55%] xl:w-[52%]">
            <img
              src="/hero-pool-brandpink.jpg"
              alt=""
              aria-hidden="true"
              className="w-full h-full object-cover object-[55%_70%]"
            />
            {/* Soft left fade only — image stays bright on the right */}
            <div className="absolute inset-y-0 left-0 w-[35%] bg-gradient-to-r from-brand-navy to-transparent pointer-events-none" />
          </div>
        </div>

        <div className="section-container relative py-6 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="animate-fade-up">
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.1] tracking-tight mb-2 text-white drop-shadow-lg">
                Pool Service in Port St. Lucie,{" "}
                <span className="text-brand-pink">FL</span>
              </h1>

              {/* Google reviews trust badge — visually placed near the H1 but OUTSIDE it
                  so Google sees a clean H1 of "Pool Service in Port St. Lucie, FL". */}
              <div
                className="inline-flex items-center gap-1.5 align-middle mb-2 bg-white rounded-full pl-1.5 pr-3 py-1 shadow-lg whitespace-nowrap"
                data-testid="hero-google-rating"
                aria-label="Rated 5.0 out of 5 on Google"
              >
                <span className="w-5 h-5 grid place-items-center" aria-hidden="true">
                  <svg viewBox="0 0 48 48" className="w-4 h-4">
                    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.7 6.5 29.6 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c10.5 0 19.4-7.6 19.4-19.5 0-1.2-.1-2.3-.3-3.5z" />
                    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8c1.8-4.4 6.1-7.5 11.1-7.5 3 0 5.8 1.1 7.9 3l5.7-5.7C34.7 6.5 29.6 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z" />
                    <path fill="#4CAF50" d="M24 43.5c5.4 0 10.3-2 14-5.3l-6.5-5.3c-1.9 1.3-4.4 2.1-7.5 2.1-5.2 0-9.6-3.3-11.3-7.9l-6.6 5C9.5 39 16.2 43.5 24 43.5z" />
                    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.8l6.5 5.3c4.6-4.2 7.3-10.4 7.3-17.1 0-1.2-.1-2.3-.3-3.5z" />
                  </svg>
                </span>
                <span className="flex items-center gap-0.5" aria-hidden="true">
                  {[0,1,2,3,4].map((i) => (
                    <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                  ))}
                </span>
                <span className="text-xs font-bold text-brand-navy ml-0.5">5.0</span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-extrabold text-white leading-tight mb-3 drop-shadow-lg">
                Crystal Clear Pools, Every Week.
              </h2>

              <p
                className="text-sm sm:text-base text-white font-semibold mb-2"
                style={{ textShadow: "0 2px 6px rgba(0,0,0,0.85), 0 1px 2px rgba(0,0,0,0.9)" }}
              >
                Serving the entire Treasure Coast — based in Port St. Lucie.
              </p>
              <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="hero-region-line">
                <span
                  className="text-sm sm:text-base text-white font-medium"
                  style={{ textShadow: "0 2px 6px rgba(0,0,0,0.85)" }}
                >
                  Also serving:
                </span>
                <Link
                  to="/pool-service-fort-pierce"
                  className="inline-flex items-center px-3 py-1 rounded-full bg-white text-brand-navy text-sm font-bold shadow-md hover:bg-brand-pink hover:text-white transition-colors"
                >
                  Fort Pierce
                </Link>
                <Link
                  to="/pool-service-stuart"
                  className="inline-flex items-center px-3 py-1 rounded-full bg-white text-brand-navy text-sm font-bold shadow-md hover:bg-brand-pink hover:text-white transition-colors"
                >
                  Stuart
                </Link>
                <Link
                  to="/pool-service-palm-city"
                  className="inline-flex items-center px-3 py-1 rounded-full bg-white text-brand-navy text-sm font-bold shadow-md hover:bg-brand-pink hover:text-white transition-colors"
                >
                  Palm City
                </Link>
              </div>

              {/* Hidden on mobile — same copy already lives in the intro strip below the hero */}
              <p className="hidden lg:block text-lg text-white/90 leading-relaxed mb-8 max-w-xl drop-shadow">
                Family-owned pool cleaning and maintenance company serving Port St. Lucie,
                Fort Pierce, Stuart, and the Treasure Coast.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-10 sm:mt-0 mb-4 sm:mb-10">
                <a href="tel:7722890405" className="btn-primary text-base sm:text-lg" data-testid="hero-cta-phone">
                  <Phone className="w-5 h-5" /> Tap to Call · 772-289-0405
                </a>
                <Link to="/contact" className="btn-secondary bg-white text-brand-navy hover:bg-white/90" data-testid="hero-cta-estimate">
                  Get Free Estimate <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="hidden md:grid grid-cols-3 gap-4 max-w-md">
                {[
                  { n: "20+", l: "Years Experience" },
                  { n: "15+", l: "Service Areas" },
                  { n: "100%", l: "Transparency" },
                ].map((s, i) => (
                  <div key={i}>
                    <div className="text-2xl font-heading font-extrabold text-white drop-shadow">{s.n}</div>
                    <div className="text-xs text-white/80 font-medium">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO STRIP — moved out of the hero so it sits right under the truck image. */}
      <section className="bg-white py-10 sm:py-12 border-b border-slate-100" data-testid="home-intro-strip">
        <div className="section-container max-w-4xl">
          <p className="text-lg sm:text-xl text-slate-700 leading-relaxed text-center">
            We provide <span className="font-semibold text-brand-navy">reliable weekly pool service</span>,
            <span className="font-semibold text-brand-navy"> expert repairs</span>, and
            <span className="font-semibold text-brand-navy"> detailed before-and-after reports</span> so you always
            know your pool is taken care of.
          </p>
        </div>
      </section>

      {/* LOOK FOR THE PINK TRUCK — local recognition */}
      <section
        className="bg-gradient-to-br from-brand-pink/10 via-white to-brand-pink/5 py-16 sm:py-20"
        data-testid="pink-truck-section"
      >
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="overline mb-3 text-brand-pink">Spot Us Around Town</div>
              <h2 className="text-3xl sm:text-5xl font-extrabold leading-tight mb-5 text-brand-navy">
                Look for the <span className="text-brand-pink">Flamingo Pink Truck</span>
              </h2>
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                You can&rsquo;t miss us. Our signature pink Pool Guru truck is a familiar sight in driveways
                across Port St. Lucie, Fort Pierce, Stuart, Palm City, and Jensen Beach. If you see the truck
                in your neighborhood, give us a wave — and call <a href="tel:7722890405" className="text-brand-pink font-semibold hover:underline">772-289-0405</a> to add your pool to the route.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="tel:7722890405" className="btn-primary" data-testid="truck-cta-phone">
                  <Phone className="w-4 h-4" /> Call 772-289-0405
                </a>
                <Link to="/contact" className="btn-outline" data-testid="truck-cta-estimate">
                  Get a Free Estimate <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="order-1 lg:order-2 relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl ring-4 ring-brand-pink/20">
                <img
                  src="https://img1.wsimg.com/isteam/ip/6251f5ed-3ec2-463f-9bc9-248d796842b9/ChatGPT%20Image%20Apr%2017%2C%202026%2C%2004_41_51%20PM.png/:/rs=w:1280"
                  alt="The Pool Guru pink service truck"
                  className="w-full h-auto block"
                  data-testid="pink-truck-image"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-brand-pink text-white rounded-2xl px-5 py-3 shadow-xl shadow-pink-500/30 rotate-3 hidden sm:block">
                <div className="text-xs font-bold opacity-90 tracking-wider">SPOTTED IN</div>
                <div className="text-lg font-heading font-extrabold whitespace-nowrap">5 Treasure Coast cities</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* POOL TIPS TEASER — educational videos */}
      <PoolTipsTeaser />

      {/* TRUST BAR */}
      <section className="border-y border-slate-100 bg-white py-8">
        <div className="section-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: CheckCircle2, label: "Weekly Service" },
              { icon: Camera, label: "Before/After Photos" },
              { icon: Shield, label: "Licensed & Insured" },
              { icon: Star, label: "5-Star Reviews" },
            ].map((t, i) => {
              const Icon = t.icon;
              return (
                <div key={i} className="flex items-center gap-3 justify-center">
                  <Icon className="w-5 h-5 text-brand-pink" />
                  <span className="font-semibold text-brand-navy text-sm">{t.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24" data-testid="services-section">
        <div className="section-container">
          <div className="max-w-2xl mb-12">
            <div className="overline mb-3 flex items-center gap-2">
              <Mascot size={22} testId="services-mascot" /> What We Do
            </div>
            <h2 className="text-3xl sm:text-5xl font-extrabold mb-4">
              Complete pool care, from weekly service to major repairs.
            </h2>
            <p className="text-lg text-slate-600">
              Everything your pool needs, delivered by a team that knows Florida pools inside and out.
            </p>
            {/* Mobile-only quick bullets — fills whitespace and improves scannability */}
            <ul className="lg:hidden mt-6 space-y-3" data-testid="services-mobile-bullets">
              {[
                "Weekly cleaning, chemistry & filter care",
                "Accurate before and after reporting",
                "Same day service",
                "Best rates and timely service daily",
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-brand-pink mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700 text-base">{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => {
              const Icon = iconMap[s.icon] || Droplets;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="group bg-white rounded-2xl border border-slate-200 p-8 hover:-translate-y-1 hover:shadow-xl hover:border-brand-pink/30 transition-all duration-300"
                  data-testid={`service-card-${s.slug}`}
                >
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand-pink/10 to-brand-blue/10 flex items-center justify-center mb-5 group-hover:from-brand-pink group-hover:to-brand-pink-hover transition-all">
                    <Icon className="w-6 h-6 text-brand-pink group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{s.name}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    {s.short_description}
                  </p>
                  <span className="inline-flex items-center gap-2 text-brand-blue font-semibold text-sm group-hover:gap-3 transition-all">
                    Learn more about {s.name.toLowerCase()} <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* BRAND STORY VIDEO — click to play */}
      <section
        className="pt-4 pb-24 bg-gradient-to-b from-brand-navy via-[#0f2847] to-brand-navy text-white"
        data-testid="brand-video-section"
      >
        <div className="section-container">
          <div className="max-w-3xl mx-auto text-center mb-8">
            <div className="overline mb-3 flex items-center justify-center gap-2 text-brand-pink">
              <Mascot size={22} testId="video-mascot" /> Watch Our Story
            </div>
            <h2 className="text-3xl md:text-4xl font-bold !text-white">
              See why families across the Treasure Coast call The Pool Guru
            </h2>
          </div>
          <div className="max-w-md mx-auto rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 bg-black">
            <video
              data-testid="brand-story-video"
              className="w-full h-auto block"
              controls
              preload="metadata"
              playsInline
              poster="https://customer-assets.emergentagent.com/job_0a06a0d5-58a4-4a28-a223-abff303efd5e/artifacts/lf7vi7ho_Screen%20Shot%202026-05-14%20at%2012.23.08%20PM.png"
            >
              <source
                src="https://customer-assets.emergentagent.com/job_0a06a0d5-58a4-4a28-a223-abff303efd5e/artifacts/vpysf54a_88.2-invideo-video_tools.mp4"
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* BEFORE/AFTER */}
      <section className="py-24 bg-slate-50">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="overline mb-3">Green to Clean</div>
              <h2 className="text-3xl sm:text-5xl font-extrabold mb-6">
                From murky green to crystal clear — fast.
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Florida's heat can turn a pool green almost overnight. Our commercial-grade equipment
                and proven recovery process bring pools back to crystal-clear condition in just a few days.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Complete water chemistry assessment",
                  "Shock treatment and algae eradication",
                  "Deep brushing of all surfaces",
                  "Filter cleaning and water testing",
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-pink mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{b}</span>
                  </li>
                ))}
              </ul>
              <Link to="/services/green-pool-cleanup" className="btn-primary" data-testid="green-pool-cta">
                Green Pool Recovery <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/3d6819ba-8f3a-4441-b1a2-b67c0cd09ed7/images/cf8d343b1d52c3b60f0f28a8a43388fb1bea31fa53e01b9ab7a8aa2349b34c22.png"
                alt="Before and after pool cleaning"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* LOCATIONS */}
      <section className="py-24">
        <div className="section-container">
          <div className="max-w-2xl mb-12">
            <div className="overline mb-3 flex items-center gap-2">
              <Mascot size={22} testId="locations-mascot" /> Service Areas
            </div>
            <h2 className="text-3xl sm:text-5xl font-extrabold mb-4">
              Proudly serving the entire Treasure Coast.
            </h2>
            <p className="text-lg text-slate-600">
              We provide weekly pool service across{" "}
              <Link to="/pool-service-port-st-lucie" className="text-brand-blue font-semibold hover:text-brand-pink underline-offset-2 hover:underline">Port St. Lucie</Link>,{" "}
              <Link to="/pool-service-fort-pierce" className="text-brand-blue font-semibold hover:text-brand-pink underline-offset-2 hover:underline">Fort Pierce</Link>,{" "}
              <Link to="/pool-service-stuart" className="text-brand-blue font-semibold hover:text-brand-pink underline-offset-2 hover:underline">Stuart</Link>,{" "}
              <Link to="/pool-service-palm-city" className="text-brand-blue font-semibold hover:text-brand-pink underline-offset-2 hover:underline">Palm City</Link>, and surrounding Treasure Coast communities. From Port St. Lucie to Stuart, we bring consistent, reliable pool care to local homeowners across the region.
            </p>
            {/* Mobile-only quick bullets — fills whitespace before pill grid */}
            <ul className="lg:hidden mt-6 space-y-3" data-testid="locations-mobile-bullets">
              {[
                { name: "Port St. Lucie", slug: "port-st-lucie" },
                { name: "Fort Pierce", slug: "fort-pierce" },
                { name: "Stuart", slug: "stuart" },
                { name: "Palm City", slug: "palm-city" },
              ].map((c) => (
                <li key={c.slug} className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-brand-pink mt-0.5 flex-shrink-0" />
                  <Link
                    to={`/pool-service-${c.slug}`}
                    className="text-slate-700 text-base font-semibold hover:text-brand-pink"
                    data-testid={`mobile-city-bullet-${c.slug}`}
                  >
                    Pool service in {c.name}
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/locations"
              className="lg:hidden mt-4 inline-flex items-center gap-2 text-brand-blue font-bold text-sm hover:text-brand-pink"
              data-testid="mobile-locations-link"
            >
              See all 15 service areas <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {cities.map((c) => (
              <Link
                key={c.slug}
                to={`/pool-service-${c.slug}`}
                className="group px-4 py-3 bg-white border border-slate-200 rounded-xl hover:border-brand-pink hover:shadow-md transition-all text-center"
                data-testid={`location-pill-${c.slug}`}
              >
                <div className="font-semibold text-sm text-brand-navy group-hover:text-brand-pink transition-colors">
                  {c.name}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">{c.region}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY FL POOL SERVICE — long-form depth */}
      <section className="py-24 bg-slate-50" data-testid="why-fl-section">
        <div className="section-container">
          <div className="max-w-3xl">
            <div className="overline mb-3 flex items-center gap-2">
              <Mascot size={22} testId="why-fl-mascot" /> Florida Pool Reality
            </div>
            <h2 className="text-3xl sm:text-5xl font-extrabold mb-4">
              Why Pool Service is Critical in Florida
            </h2>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              A swimming pool in Port St. Lucie, Fort Pierce, or Stuart isn't the same as a pool anywhere else in the country. Florida's climate puts unique pressure on every pool — and DIY maintenance, no matter how diligent, almost never keeps up.
            </p>
            <ul className="space-y-4 mb-6">
              {[
                ["Intense UV burns through chlorine.", "Without proper stabilizer levels, chlorine residual disappears in hours, not days. Most green pools start exactly here."],
                ["Summer storms throw chemistry off balance.", "A heavy afternoon downpour dilutes calcium and stabilizer, drops pH, and washes organic debris into the pool — all in a single afternoon."],
                ["Year-round algae pressure.", "Florida doesn't have a real pool off-season. Pollen, oak debris, and warm water keep algae spores active 365 days a year."],
                ["Equipment lives in coastal humidity.", "Pumps, heaters, and metal fittings on the Treasure Coast corrode faster than inland equivalents. Catching issues early extends equipment life by years."],
              ].map(([title, desc], i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`why-fl-bullet-${i}`}>
                  <CheckCircle2 className="w-6 h-6 text-brand-pink mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700"><strong className="text-brand-navy">{title}</strong> {desc}</span>
                </li>
              ))}
            </ul>
            <p className="text-lg text-slate-600 leading-relaxed">
              Weekly professional pool cleaning isn't a luxury in Florida — it's the difference between a pool that's swim-ready year-round and a pool that demands constant emergency intervention. That's why so many local homeowners choose a dependable Treasure Coast pool company over fighting the climate alone.
            </p>
          </div>
        </div>
      </section>

      {/* OUR WEEKLY PROCESS */}
      <section className="py-24" data-testid="our-process-section">
        <div className="section-container">
          <div className="max-w-3xl mb-10">
            <div className="overline mb-3 flex items-center gap-2">
              <Mascot size={22} testId="process-mascot" /> Our Process
            </div>
            <h2 className="text-3xl sm:text-5xl font-extrabold mb-4">
              Our Weekly Pool Service Process
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Same day every week. Same documented process. Same family-owned local pool company you can call directly when you need us. Every visit covers the full scope of pool cleaning, pool maintenance, and equipment care.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-4">
            {[
              ["Surface skimming & waterline cleaning", "Leaves, bugs, pollen, and debris removed before they sink and stain the plaster."],
              ["Wall and step brushing", "Corners, returns, ladders — where algae actually starts before it spreads across the pool."],
              ["Skimmer & pump-basket emptying", "Full baskets choke flow and damage motors. Fundamental, but constantly missed by other companies."],
              ["Full chemistry panel", "Free chlorine, total chlorine, pH, alkalinity, calcium hardness, stabilizer, and salt for saltwater systems."],
              ["Precise chemical dosing", "We add only what your readings require — never overdose, never upsell."],
              ["Filter pressure check & equipment glance-over", "Backwash or media-clean as needed; check pump, leaks, timer, and heater."],
              ["Vacuuming as needed", "Not as a checkbox. If there's debris on the bottom we vacuum, otherwise we skip it."],
              ["Before-and-after photos & written summary", "Every visit, sent to you the same day with chemistry readings and any equipment notes."],
            ].map(([title, desc], i) => (
              <div key={i} className="flex items-start gap-3 py-4 border-b border-slate-100 last:border-0" data-testid={`process-step-${i}`}>
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-brand-pink to-brand-pink-hover text-white font-bold flex items-center justify-center text-sm">
                  {i + 1}
                </div>
                <div>
                  <div className="font-bold text-brand-navy">{title}</div>
                  <p className="text-slate-600 text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 bg-gradient-to-br from-brand-navy via-[#0f2847] to-brand-navy text-white" data-testid="why-choose-section">
        <div className="section-container">
          <div className="max-w-3xl mb-10">
            <div className="overline mb-3 flex items-center gap-2 text-brand-pink">
              <Mascot size={22} testId="why-mascot" /> Why Pool Guru
            </div>
            <h2 className="text-3xl sm:text-5xl font-extrabold mb-4 text-white">
              Why Homeowners on the Treasure Coast Choose Pool Guru
            </h2>
            <p className="text-lg text-white/80 leading-relaxed">
              We're not the cheapest pool service on the Treasure Coast, and we're not the biggest. What we are is the most consistent — and consistency is the entire point of pool maintenance.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              ["20+ years experience", "Two decades on the Treasure Coast. We've seen every Florida pool problem and know how to handle each without panic or thousand-dollar emergency quotes."],
              ["Family-owned & local", "When you call, you reach the same team. We're not a franchise, not a corporate route, not a side business. Local pool company, local accountability."],
              ["Transparent reporting", "Before-and-after photos and a written summary after every single visit. You see exactly what your pool looks like and what we did."],
              ["No long-term contracts", "We earn your next visit by doing this one well. No early-termination fees. (Almost no one leaves.)"],
            ].map(([title, desc], i) => (
              <div key={i} className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors" data-testid={`why-card-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-brand-pink flex items-center justify-center mb-4">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div className="font-heading font-extrabold text-xl mb-2">{title}</div>
                <p className="text-white/75 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="section-container">
          <div className="max-w-2xl mb-12">
            <div className="overline mb-3 flex items-center gap-2">
              <Mascot size={22} testId="reviews-mascot" /> What Customers Say
            </div>
            <h2 className="text-3xl sm:text-5xl font-extrabold mb-4">
              Trusted by homeowners across the Treasure Coast.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {reviews.slice(0, 3).map((r) => (
              <div
                key={r.id}
                className="bg-white rounded-2xl border border-slate-200 p-7 hover:shadow-lg transition-shadow"
                data-testid={`home-review-${r.id}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(r.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-slate-700 leading-relaxed mb-5 italic">"{r.comment}"</p>
                <div className="pt-5 border-t border-slate-100">
                  <div className="font-bold text-brand-navy">{r.name}</div>
                  <div className="text-sm text-slate-500">{r.location}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/reviews" className="btn-secondary" data-testid="home-reviews-cta">
              Read all reviews <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
